// components/GuestInfoForm.js
import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Search, Users, ChevronDown, ChevronRight } from 'lucide-react';
import ClientSearch from './ClientSearch';

const GuestInfoForm = ({
  guestInfo,
  setGuestInfo,
  selectedCheckIn,
  selectedCheckOut,
  selectedRoomType,
  selectedOccupancy,
  selectedRoomCount,           // 🔵 AJOUTÉ
  selectedRoomOccupancies,     // 🔵 AJOUTÉ
  selectedRoomIds,             // ✅ NOUVEAU
  rooms,                       // ✅ NOUVEAU
  selectedOptions,
  availableOptions,
  roomTypes,
  displayPrices = {},          // ✅ AJOUTÉ POUR CORRIGER LE PRIX
  calculateTotal,
  calculateOptionsTotal,
  differenceInDays,
  format,
  fr,
  language,
  t,
  dateLocale,
  customAnswers,
  setCustomAnswers,
  adminMode = false,
  selectedClientId,
  setSelectedClientId,
  appliedCoupon,
  calculateCouponDiscount,
  guestsDetails = [],      // ✅ RENOMMÉ
  setGuestsDetails,        // ✅ RENOMMÉ
  onIdentityFieldBlur
}) => {
  
  const [customQuestions, setCustomQuestions] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);  // ✅ AJOUTÉ
  const [showGuestsSection, setShowGuestsSection] = useState(false);  // ✅ NOUVEAU

// ✅ Mémoïser les calculs pour éviter les recalculs à chaque render
  const optionsTotal = React.useMemo(() => {
    return calculateOptionsTotal ? calculateOptionsTotal() : 0;
  }, [calculateOptionsTotal]);

  const totalPrice = React.useMemo(() => {
    return calculateTotal ? calculateTotal() : 0;
  }, [calculateTotal]);

  // ✅ AJOUTÉ - Fonction pour sélectionner un client
  const handleSelectClient = (client) => {
    setGuestInfo({
      name: client.name || '',
      firstName: client.firstName || '',
      company: client.company || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      postalCode: client.postalCode || '',
      country: client.country || (language === 'fr' ? 'France' : 'United States'),
      message: ''
    });
      setSelectedClientId(client.id);  // ✅ STOCKER L'ID
    setShowClientSearch(false);
  };
  
  // Charger les questions personnalisées
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const q = query(
          collection(db, 'custom_questions'),
          where('active', '==', true),
          orderBy('order', 'asc')
        );
        const snapshot = await getDocs(q);
        const questionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomQuestions(questionsData);
      } catch (error) {
        console.error('Erreur chargement questions:', error);
      }
    };
    loadQuestions();
  }, []);
  
  const handleGuestChange = (index, field, value) => {
    if (!setGuestsDetails) return;
    const newGuestsDetails = [...guestsDetails];
    newGuestsDetails[index][field] = value;
    setGuestsDetails(newGuestsDetails);
  };
  
  const roomType = roomTypes.find(rt => rt.id === selectedRoomType);
  const nights = differenceInDays(selectedCheckOut, selectedCheckIn);
  const occupancy = selectedOccupancy[selectedRoomType] || 2;
  
  // ✅ AJOUTÉ : Fonction pour calculer le nombre total de personnes
  const getTotalGuestsCount = () => {
    if (adminMode && selectedRoomIds) {
      // Mode admin : calculer depuis selectedRoomIds + selectedOccupancy
      return selectedRoomIds.reduce((total, roomId) => {
        const roomOccupancy = selectedOccupancy[roomId] || 2;
        return total + roomOccupancy;
      }, 0);
    } else {
      // Mode public : calculer depuis selectedRoomCount + selectedRoomOccupancies
      let total = 0;
      Object.entries(selectedRoomCount || {}).forEach(([typeId, count]) => {
        if (count > 0) {
          const occupancies = selectedRoomOccupancies?.[typeId] || [];
          total += occupancies.length > 0 
            ? occupancies.reduce((sum, occ) => sum + occ, 0)
            : count * (selectedOccupancy?.[typeId] || 2);
        }
      });
      return total;
    }
  };
  
  // ✅ NOUVEAU : Gérer le tableau guests (initialisation + synchronisation)
  useEffect(() => {
    if (!setGuestsDetails) return;
    
    const totalAdults = getTotalGuestsCount();
    
    // Cas 1 : Tableau vide ou mauvaise taille → Reconstruire complètement
    if (guestsDetails.length !== totalAdults) {
      const newGuestsDetails = [];
      for (let i = 0; i < totalAdults; i++) {
        newGuestsDetails.push({
          firstName: i === 0 ? (guestInfo.firstName || '') : (guestsDetails[i]?.firstName || ''),
          lastName: i === 0 ? (guestInfo.name || '') : (guestsDetails[i]?.lastName || ''),
          role: i === 0 ? 'main' : 'companion'
        });
      }
      setGuestsDetails(newGuestsDetails);
    }
    // Cas 2 : Bonne taille mais guestsDetails[0] désynchronisé → Mettre à jour juste guestsDetails[0]
    else if (guestsDetails.length > 0 && 
             (guestsDetails[0]?.firstName !== guestInfo.firstName || guestsDetails[0]?.lastName !== guestInfo.name)) {
      const newGuestsDetails = [...guestsDetails];
      newGuestsDetails[0] = {
        firstName: guestInfo.firstName || '',
        lastName: guestInfo.name || '',
        role: 'main'
      };
      setGuestsDetails(newGuestsDetails);
    }
}, [selectedRoomCount, selectedRoomOccupancies, selectedRoomIds, selectedOccupancy, guestInfo.firstName, guestInfo.name]);
  const getRoomPrice = () => {
    // Utiliser les prix calculés (computed_prices) ou le prix de base
    const price = displayPrices[roomType.id] || roomType.basePrice;
    return nights * price;
  };
  
  const countries = language === 'fr' ? [
    // France en premier
    { value: 'France', label: 'France' },
    // UE — ordre alphabétique français
    { value: 'Allemagne', label: 'Allemagne' },
    { value: 'Autriche', label: 'Autriche' },
    { value: 'Belgique', label: 'Belgique' },
    { value: 'Bulgarie', label: 'Bulgarie' },
    { value: 'Chypre', label: 'Chypre' },
    { value: 'Croatie', label: 'Croatie' },
    { value: 'Danemark', label: 'Danemark' },
    { value: 'Espagne', label: 'Espagne' },
    { value: 'Estonie', label: 'Estonie' },
    { value: 'Finlande', label: 'Finlande' },
    { value: 'Grèce', label: 'Grèce' },
    { value: 'Hongrie', label: 'Hongrie' },
    { value: 'Irlande', label: 'Irlande' },
    { value: 'Italie', label: 'Italie' },
    { value: 'Lettonie', label: 'Lettonie' },
    { value: 'Lituanie', label: 'Lituanie' },
    { value: 'Luxembourg', label: 'Luxembourg' },
    { value: 'Malte', label: 'Malte' },
    { value: 'Pays-Bas', label: 'Pays-Bas' },
    { value: 'Pologne', label: 'Pologne' },
    { value: 'Portugal', label: 'Portugal' },
    { value: 'République tchèque', label: 'République tchèque' },
    { value: 'Roumanie', label: 'Roumanie' },
    { value: 'Slovaquie', label: 'Slovaquie' },
    { value: 'Slovénie', label: 'Slovénie' },
    { value: 'Suède', label: 'Suède' },
    // Europe hors UE
    { value: 'Andorre', label: 'Andorre' },
    { value: 'Islande', label: 'Islande' },
    { value: 'Liechtenstein', label: 'Liechtenstein' },
    { value: 'Monaco', label: 'Monaco' },
    { value: 'Norvège', label: 'Norvège' },
    { value: 'Royaume-Uni', label: 'Royaume-Uni' },
    { value: 'Suisse', label: 'Suisse' },
    // Reste du monde
    { value: 'Afrique du Sud', label: 'Afrique du Sud' },
    { value: 'Arabie Saoudite', label: 'Arabie Saoudite' },
    { value: 'Argentine', label: 'Argentine' },
    { value: 'Australie', label: 'Australie' },
    { value: 'Brésil', label: 'Brésil' },
    { value: 'Canada', label: 'Canada' },
    { value: 'Chine', label: 'Chine' },
    { value: 'Corée du Sud', label: 'Corée du Sud' },
    { value: 'États-Unis', label: 'États-Unis' },
    { value: 'Inde', label: 'Inde' },
    { value: 'Israël', label: 'Israël' },
    { value: 'Japon', label: 'Japon' },
    { value: 'Maroc', label: 'Maroc' },
    { value: 'Mexique', label: 'Mexique' },
    { value: 'Nouvelle-Zélande', label: 'Nouvelle-Zélande' },
    { value: 'Singapour', label: 'Singapour' },
    { value: 'Tunisie', label: 'Tunisie' },
    { value: 'Turquie', label: 'Turquie' },
    { value: 'Émirats arabes unis', label: 'Émirats arabes unis' },
    { value: 'Autre', label: 'Autre' }
  ] : [
    // France first (destination country)
    { value: 'France', label: 'France' },
    // EU — alphabetical English
    { value: 'Austria', label: 'Austria' },
    { value: 'Belgium', label: 'Belgium' },
    { value: 'Bulgaria', label: 'Bulgaria' },
    { value: 'Croatia', label: 'Croatia' },
    { value: 'Cyprus', label: 'Cyprus' },
    { value: 'Czech Republic', label: 'Czech Republic' },
    { value: 'Denmark', label: 'Denmark' },
    { value: 'Estonia', label: 'Estonia' },
    { value: 'Finland', label: 'Finland' },
    { value: 'Germany', label: 'Germany' },
    { value: 'Greece', label: 'Greece' },
    { value: 'Hungary', label: 'Hungary' },
    { value: 'Ireland', label: 'Ireland' },
    { value: 'Italy', label: 'Italy' },
    { value: 'Latvia', label: 'Latvia' },
    { value: 'Lithuania', label: 'Lithuania' },
    { value: 'Luxembourg', label: 'Luxembourg' },
    { value: 'Malta', label: 'Malta' },
    { value: 'Netherlands', label: 'Netherlands' },
    { value: 'Poland', label: 'Poland' },
    { value: 'Portugal', label: 'Portugal' },
    { value: 'Romania', label: 'Romania' },
    { value: 'Slovakia', label: 'Slovakia' },
    { value: 'Slovenia', label: 'Slovenia' },
    { value: 'Spain', label: 'Spain' },
    { value: 'Sweden', label: 'Sweden' },
    // Europe non-EU
    { value: 'Andorra', label: 'Andorra' },
    { value: 'Iceland', label: 'Iceland' },
    { value: 'Liechtenstein', label: 'Liechtenstein' },
    { value: 'Monaco', label: 'Monaco' },
    { value: 'Norway', label: 'Norway' },
    { value: 'Switzerland', label: 'Switzerland' },
    { value: 'United Kingdom', label: 'United Kingdom' },
    // Rest of world
    { value: 'Argentina', label: 'Argentina' },
    { value: 'Australia', label: 'Australia' },
    { value: 'Brazil', label: 'Brazil' },
    { value: 'Canada', label: 'Canada' },
    { value: 'China', label: 'China' },
    { value: 'India', label: 'India' },
    { value: 'Israel', label: 'Israel' },
    { value: 'Japan', label: 'Japan' },
    { value: 'Mexico', label: 'Mexico' },
    { value: 'Morocco', label: 'Morocco' },
    { value: 'New Zealand', label: 'New Zealand' },
    { value: 'Saudi Arabia', label: 'Saudi Arabia' },
    { value: 'Singapore', label: 'Singapore' },
    { value: 'South Africa', label: 'South Africa' },
    { value: 'South Korea', label: 'South Korea' },
    { value: 'Tunisia', label: 'Tunisia' },
    { value: 'Turkey', label: 'Turkey' },
    { value: 'United Arab Emirates', label: 'United Arab Emirates' },
    { value: 'United States', label: 'United States' },
    { value: 'Other', label: 'Other' }
  ];
  
  const roomName = language === 'fr' ? 
    (roomType?.nameFr || roomType?.name) : 
    (roomType?.nameEn || roomType?.name);
  
  return (
    <div>
      <h2 className="text-2xl font-light mb-6 text-center">
        {t.yourInformation}
      </h2>
      
      {/* ✅ AJOUTÉ - Bouton recherche client en mode admin */}
      {adminMode && (
        <div className="max-w-lg mx-auto mb-6">
          <button
            type="button"
            onClick={() => setShowClientSearch(true)}
            className="w-full py-3 border-2 border-black bg-yellow-400 hover:bg-yellow-500 rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors"
          >
            <Search size={20} />
            Rechercher un client existant
          </button>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Cliquez pour rechercher et pré-remplir les informations d'un client déjà enregistré
          </p>
        </div>
      )}
      
      <div className="max-w-lg mx-auto space-y-4">
        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.lastName} *</label>
          <input
            type="text"
            value={guestInfo.name}
            onChange={(e) => setGuestInfo({...guestInfo, name: e.target.value})}
            onBlur={() => onIdentityFieldBlur && onIdentityFieldBlur()}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
            required
          />
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.firstName} *</label>
          <input
            type="text"
            value={guestInfo.firstName}
            onChange={(e) => setGuestInfo({...guestInfo, firstName: e.target.value})}
            onBlur={() => onIdentityFieldBlur && onIdentityFieldBlur()}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
            required
          />
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.company}</label>
          <input 
            type="text"
            value={guestInfo.company}
            onChange={(e) => setGuestInfo({...guestInfo, company: e.target.value})}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
          />
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.email} *</label>
          <input
            type="email"
            value={guestInfo.email}
            onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
            onBlur={() => onIdentityFieldBlur && onIdentityFieldBlur()}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
            pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
            title={language === 'fr' ? 'Veuillez entrer une adresse email valide' : 'Please enter a valid email address'}
            required
          />
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.phone} *</label>
          <input
            type="tel"
            value={guestInfo.phone}
            onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
            onBlur={() => onIdentityFieldBlur && onIdentityFieldBlur()}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
            pattern="[\+]?[0-9\s]+"
            title={language === 'fr' ? 'Numéro de téléphone (chiffres et + uniquement)' : 'Phone number (numbers and + only)'}
            required
          />
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.address} *</label>
          <input 
            type="text"
            value={guestInfo.address}
            onChange={(e) => setGuestInfo({...guestInfo, address: e.target.value})}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm uppercase tracking-wider mb-2">{t.city} *</label>
            <input 
              type="text"
              value={guestInfo.city}
              onChange={(e) => setGuestInfo({...guestInfo, city: e.target.value})}
              className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm uppercase tracking-wider mb-2">{t.postalCode} *</label>
            <input 
              type="text"
              value={guestInfo.postalCode}
              onChange={(e) => setGuestInfo({...guestInfo, postalCode: e.target.value})}
              className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.country}</label>
          <select 
            value={guestInfo.country}
            onChange={(e) => setGuestInfo({...guestInfo, country: e.target.value})}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
          >
            {countries.map(country => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm uppercase tracking-wider mb-2">{t.message}</label>
          <textarea 
            value={guestInfo.message}
            onChange={(e) => setGuestInfo({...guestInfo, message: e.target.value})}
            className="w-full p-3 border border-gray-300 focus:border-black outline-none h-24 rounded"
            placeholder={t.additionalInfo}
          />
        </div>

        {/* Questions personnalisées */}
        {customQuestions.length > 0 && (
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-sm uppercase tracking-wider">
              {language === 'fr' ? 'Informations complémentaires' : 'Additional Information'}
            </h3>
            {customQuestions.map(question => {
              const questionText = language === 'fr' ? 
                question.questionFr : 
                (question.questionEn || question.questionFr);
              
              return (
                <div key={question.id}>
                  <label className="block text-sm uppercase tracking-wider mb-2">
                    {questionText} {question.required && '*'}
                  </label>
                  {question.type === 'text' ? (
                    <input
                      type="text"
                      value={customAnswers[question.id] || ''}
                      onChange={(e) => setCustomAnswers({...customAnswers, [question.id]: e.target.value})}
                      className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
                      required={question.required}
                    />
                  ) : (
                    <select
                      value={customAnswers[question.id] || ''}
                      onChange={(e) => setCustomAnswers({...customAnswers, [question.id]: e.target.value})}
                      className="w-full p-3 border border-gray-300 focus:border-black outline-none rounded"
                      required={question.required}
                    >
                      <option value="">-</option>
                      <option value={language === 'fr' ? 'Oui' : 'Yes'}>
                        {language === 'fr' ? 'Oui' : 'Yes'}
                      </option>
                      <option value={language === 'fr' ? 'Non' : 'No'}>
                        {language === 'fr' ? 'Non' : 'No'}
                      </option>
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ✅ NOUVEAU : Section Accompagnants (facultatif) */}
        {!adminMode && getTotalGuestsCount() > 1 && (
          <div className="pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowGuestsSection(!showGuestsSection)}
              className="w-full flex items-center justify-between text-sm uppercase tracking-wider font-medium hover:text-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Users size={16} />
                <span>{language === 'fr' ? 'Accompagnants' : 'Companions'}</span>
                <span className="text-xs font-normal text-gray-500">
                  ({language === 'fr' ? 'facultatif' : 'optional'})
                </span>
              </div>
              {showGuestsSection ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            
            {showGuestsSection && (
              <div className="mt-4 space-y-4">
                <p className="text-xs text-gray-600">
                  {language === 'fr' 
                    ? 'La législation nous oblige à connaître l\'identité de tous les occupants adultes. Vous pouvez renseigner ces informations maintenant ou plus tard via un lien sécurisé.'
                    : 'French law requires us to know the identity of all adult occupants. You can provide this information now or later via a secure link.'}
                </p>
                
                {guestsDetails.map((guest, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm font-medium">
                        {language === 'fr' ? 'Adulte' : 'Adult'} {index + 1}
                      </span>
                      {index === 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {language === 'fr' ? 'Réservant' : 'Main guest'}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs uppercase tracking-wider mb-2">
                          {language === 'fr' ? 'Prénom' : 'First Name'}
                        </label>
                        <input
                          type="text"
                          value={guest.firstName}
                          onChange={(e) => handleGuestChange(index, 'firstName', e.target.value)}
                          disabled={index === 0}
                          className={`w-full p-3 border border-gray-300 rounded outline-none ${
                            index === 0 ? 'bg-gray-50 text-gray-600' : 'focus:border-black'
                          }`}
                          placeholder={language === 'fr' ? 'Prénom' : 'First Name'}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs uppercase tracking-wider mb-2">
                          {language === 'fr' ? 'Nom' : 'Last Name'}
                        </label>
                        <input
                          type="text"
                          value={guest.lastName}
                          onChange={(e) => handleGuestChange(index, 'lastName', e.target.value)}
                          disabled={index === 0}
                          className={`w-full p-3 border border-gray-300 rounded outline-none ${
                            index === 0 ? 'bg-gray-50 text-gray-600' : 'focus:border-black'
                          }`}
                          placeholder={language === 'fr' ? 'Nom' : 'Last Name'}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

<div className="bg-gray-50 p-6 border border-gray-200 rounded-lg">
          <h3 className="font-medium mb-4">{t.bookingSummary}</h3>
          <div className="space-y-2 text-sm">
            
            {/* Afficher TOUS les types sélectionnés */}
            <div className="space-y-3 mb-4">
              <div className="font-medium text-base">{t.accommodation}:</div>
              
              {adminMode && selectedRoomIds ? (
                // ✅ MODE ADMIN : Afficher depuis selectedRoomIds
                selectedRoomIds.map(roomId => {
                  const room = rooms.find(r => r.id === roomId);
                  const roomTypeId = selectedOccupancy[roomId + '_type'];
                  const roomType = roomTypes.find(rt => rt.id === roomTypeId);
                  const occupancy = selectedOccupancy[roomId] || 2;
                  const nights = differenceInDays(selectedCheckOut, selectedCheckIn);
                  
                  if (!room || !roomType) return null;
                  
                  const roomName = language === 'fr' ? 
                    (roomType.nameFr || roomType.name) : 
                    (roomType.nameEn || roomType.name);
                  
                  const pricePerNight = displayPrices[roomType.id] || roomType.basePrice || 0;
                  const roomPrice = nights * pricePerNight;
                  
                  return (
                    <div key={roomId} className="bg-white p-3 rounded border border-gray-200">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium">Chambre {room.roomNumber} - {roomName}</span>
                        <span className="font-semibold">€{roomPrice}</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        {occupancy} {occupancy > 1 ? t.persons : t.person} • {nights} {nights > 1 ? t.nights : t.night} • {pricePerNight}€/{language === 'fr' ? 'nuit' : 'night'}
                      </div>
                    </div>
                  );
                })
              ) : (
                // MODE PUBLIC : Afficher depuis selectedRoomCount
                Object.entries(selectedRoomCount || {})
                  .filter(([typeId, count]) => count > 0)
                  .map(([typeId, count]) => {
                    const roomType = roomTypes.find(rt => rt.id === typeId);
                    if (!roomType) return null;
                    
                    const roomName = language === 'fr' ? 
                      (roomType.nameFr || roomType.name) : 
                      (roomType.nameEn || roomType.name);
                    
                    const occupancies = selectedRoomOccupancies?.[typeId] || [];
                    const totalGuests = occupancies.length > 0 
                      ? occupancies.reduce((sum, occ) => sum + occ, 0)
                      : count * (selectedOccupancy?.[typeId] || 2);
                    
                    const nights = differenceInDays(selectedCheckOut, selectedCheckIn);
                    
                    const pricePerNight = displayPrices[roomType.id] || roomType.basePrice || 0;
                    const roomPrice = nights * pricePerNight * count;
                    
                    return (
                      <div key={typeId} className="bg-white p-3 rounded border border-gray-200">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium">{roomName}</span>
                          <span className="font-semibold">€{roomPrice}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          {count} {count > 1 ? (language === 'fr' ? 'chambres' : 'rooms') : (language === 'fr' ? 'chambre' : 'room')} • {totalGuests} {totalGuests > 1 ? t.persons : t.person} • {nights} {nights > 1 ? t.nights : t.night}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
            
            <div className="pt-2 border-t">
              <div className="flex justify-between">
                <span>{t.dates}</span>
                <span className="font-medium">
                  {format(selectedCheckIn, 'dd/MM/yyyy')} - {format(selectedCheckOut, 'dd/MM/yyyy')}
                </span>
              </div>
              <div className="flex justify-between">
                <span>{t.numberOfNights}</span>
                <span className="font-medium">{differenceInDays(selectedCheckOut, selectedCheckIn)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t.numberOfGuests}</span>
                <span className="font-medium">
                  {getTotalGuestsCount()}
                </span>
              </div>
            </div>
            
            {selectedOptions && selectedOptions.length > 0 && (
              <div className="mt-2 pt-2 border-t">
                <div className="font-medium mb-1">{t.selectedOptions}</div>
                {selectedOptions.map(optionId => {
                  const option = availableOptions.find(o => o.id === optionId);
                  if (!option) return null;
                  const optionName = language === 'fr' ? 
                    (option.nameFr || option.name) : 
                    (option.nameEn || option.name);
                  return (
                    <div key={optionId} className="flex justify-between text-xs ml-2">
                      <span>{optionName}</span>
                    </div>
                  );
                })}
                <div className="flex justify-between mt-2">
                  <span>{t.optionsTotal}</span>
<span className="font-medium">€{optionsTotal}</span>
                </div>
              </div>
            )}
            
            {appliedCoupon && calculateCouponDiscount && (
              <div className="flex justify-between text-sm text-green-600 pt-2 border-t">
                <span>
                  {language === 'fr' ? 'Code' : 'Code'} <span className="font-mono font-semibold">{appliedCoupon.code}</span>:
                </span>
                <span className="font-medium">
                  -€{(() => {
                    let roomTotal = 0;
                    const nights = differenceInDays(selectedCheckOut, selectedCheckIn);
                    
                    if (adminMode && selectedRoomIds) {
                      // MODE ADMIN : Calculer depuis selectedRoomIds
                      selectedRoomIds.forEach(roomId => {
                        const roomTypeId = selectedOccupancy[roomId + '_type'];
                        const roomType = roomTypes.find(rt => rt.id === roomTypeId);
                        const occupancy = selectedOccupancy[roomId] || 2;
                        const pricePerNight = displayPrices[roomType?.id] || roomType?.basePrice || 0;
                        roomTotal += nights * pricePerNight;
                      });
                    } else {
                      // MODE PUBLIC : Calculer depuis selectedRoomCount
                      Object.entries(selectedRoomCount || {}).forEach(([typeId, count]) => {
                        if (count > 0) {
                          const roomType = roomTypes.find(rt => rt.id === typeId);
                          const pricePerNight = displayPrices[roomType?.id] || roomType?.basePrice || 0;
                          roomTotal += nights * pricePerNight * count;
                        }
                      });
                    }
                    
                    return calculateCouponDiscount(roomTotal).toFixed(2);
                  })()}
                </span>
              </div>
            )}
            
            <div className="flex justify-between font-bold text-lg pt-3 border-t">
              <span>{t.total}:</span>
<span className="text-black">€{totalPrice}</span>
            </div>
          </div>
        </div>

        {!guestInfo.name || !guestInfo.firstName || !guestInfo.email || !guestInfo.phone || 
         !guestInfo.address || !guestInfo.city || !guestInfo.postalCode ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              {t.requiredFields}
            </p>
          </div>
        ) : null}
      </div>
      
      {showClientSearch && (
        <ClientSearch
          onSelectClient={handleSelectClient}
          onClose={() => setShowClientSearch(false)}
        />
      )}
    </div>
  );
};

export default GuestInfoForm;